
import { supabase } from './supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';

export const getCurrentUser = async () => {
  const context: ErrorContext = {
    operation: 'getCurrentUser'
  };

  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return data.user;
  } catch (error) {
    handleError(
      error, 
      "Error getting current user", 
      ErrorSeverity.ERROR, 
      context,
      false
    );
    return null;
  }
};

export const getCurrentOrgId = async (): Promise<string | null> => {
  const context: ErrorContext = {
    operation: 'getCurrentOrgId'
  };

  try {
    const user = await getCurrentUser();
    if (!user) return null;
    
    const orgId = user.user_metadata?.organization_id;
    if (orgId) {
      console.log("Found organization ID in user metadata:", orgId);
      return orgId;
    }
    
    // Fallback: Check if we can get the organization ID from user_profiles
    const { data, error } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', user.id)
      .maybeSingle();
      
    if (error) {
      handleError(
        error, 
        "Error fetching user profile", 
        ErrorSeverity.WARNING, 
        {...context, userId: user.id},
        false
      );
      return null;
    }
    
    if (data?.organization_id) {
      console.log("Found organization ID in user_profiles:", data.organization_id);
      return data.organization_id;
    }
    
    console.warn("No organization ID found for user");
    return null;
  } catch (error) {
    handleError(
      error, 
      "Error retrieving organization ID", 
      ErrorSeverity.ERROR, 
      context,
      false
    );
    return null;
  }
};

/**
 * Securely get parent's organization ID from database verification
 * This prevents client-side metadata manipulation
 */
export const getParentOrganizationId = async (): Promise<string | null> => {
  const context: ErrorContext = {
    operation: 'getParentOrganizationId'
  };

  try {
    const user = await getCurrentUser();
    if (!user) return null;
    
    // Get parent record with organization_id from database
    const { data: parentData, error } = await supabase
      .from('parents')
      .select('organization_id')
      .eq('user_id', user.id)
      .maybeSingle();
      
    if (error) {
      handleError(
        error, 
        "Error fetching parent organization", 
        ErrorSeverity.WARNING, 
        {...context, userId: user.id},
        false
      );
      return null;
    }
    
    return parentData?.organization_id || null;
  } catch (error) {
    handleError(
      error, 
      "Error retrieving parent organization ID", 
      ErrorSeverity.ERROR, 
      context,
      false
    );
    return null;
  }
};

export const signOut = async () => {
  const context: ErrorContext = {
    operation: 'signOut'
  };

  try {
    return await supabase.auth.signOut();
  } catch (error) {
    handleError(
      error, 
      "Error during sign out", 
      ErrorSeverity.ERROR, 
      context,
      true
    );
    throw error;
  }
};
